import React from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Button,
  alpha,
  keyframes,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { Helmet } from 'react-helmet-async';
import { rarityColors, numenorColors, amberColors } from '../../theme/themeConfig';

// Import Material UI Icons
import HomeIcon from '@mui/icons-material/Home';
import BusinessIcon from '@mui/icons-material/Business';
import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import WarningIcon from '@mui/icons-material/Warning';
import PersonIcon from '@mui/icons-material/Person';
import TimelineIcon from '@mui/icons-material/Timeline';

const launched = true;

// Product Screenshots Data for Carousel
const productScreenshots = [
  {
    title: 'Complete Project Lifecycle Management',
    description:
      'Manage the full lifecycle from initial estimation through contracts to change orders. Streamline your project workflow with integrated tools designed for construction professionals.',
    lightImage: '/images/ub_contract_managemet_lm.webp',
    darkImage: '/images/ub_contract_managemet_dm.webp',
    alt: 'Contract and Change Order Management Interface',
  },
  {
    title: 'Professional Closeout Workflow',
    description:
      'A defined workflow for completing estimates designed to help estimators think through each project phase to ensure a complete, competitive bid.',
    lightImage: '/images/ub_closeout_lm.webp',
    darkImage: '/images/ub_closeout_dm.webp',
    alt: 'Estimate Closeout Workflow Interface',
  },
  {
    title: 'Intelligent Document Management',
    description:
      'Document storage with AI-assisted automatic naming and organization. Keep your project documents organized and easily accessible throughout the estimation process.',
    lightImage: '/images/ub_doc_management_lm.webp',
    darkImage: '/images/ub_doc_management_dm.webp',
    alt: 'Document Management Interface',
  },
  {
    title: 'Advanced Plan View & Takeoff Tools',
    description:
      'Full-featured annotation tool with AI chatbot assistant and easy access to assemblies for takeoff. Build custom items and assemblies and count them directly on your plans.',
    lightImage: '/images/ub_planview_lm.webp',
    darkImage: '/images/ub_planview_dm.webp',
    alt: 'Plan View and Takeoff Interface',
  },
  {
    title: 'Coming Soon: The Foundry',
    description:
      'A fully managed database of items and assemblies for users to integrate into their takeoffs. Access professionally curated construction data to speed up your estimation process and increase pricing accuracy.',
    lightImage: '/images/bf_coming_soon_lm.webp',
    darkImage: '/images/bf_coming_soon_dm.webp',
    alt: 'Build Foundry Coming Soon Interface',
  },
];

// Auto-scrolling Carousel Component
const ProductCarousel: React.FC = () => {
  const { darkMode } = useTheme();
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [isHovering, setIsHovering] = React.useState(false);
  const [expandedImage, setExpandedImage] = React.useState<number | null>(null);
  const [hoverTimer, setHoverTimer] = React.useState<NodeJS.Timeout | null>(null);

  React.useEffect(() => {
    if (isHovering || expandedImage !== null) return; // Pause auto-scroll when hovering or image expanded

    const interval = setInterval(() => {
      setCurrentIndex(prevIndex =>
        prevIndex === productScreenshots.length - 1 ? 0 : prevIndex + 1
      );
    }, 5000); // Change every 5 seconds

    return () => clearInterval(interval);
  }, [isHovering, expandedImage]);

  // Handle image hover with timer
  const handleImageMouseEnter = React.useCallback((index: number) => {
    const timer = setTimeout(() => {
      setExpandedImage(index);
    }, 1500); // Expand after 1.5 seconds of hover
    setHoverTimer(timer);
  }, []);

  const handleImageMouseLeave = React.useCallback(() => {
    if (hoverTimer) {
      clearTimeout(hoverTimer);
      setHoverTimer(null);
    }
  }, [hoverTimer]);

  // Handle image click
  const handleImageClick = React.useCallback(
    (index: number) => {
      if (hoverTimer) {
        clearTimeout(hoverTimer);
        setHoverTimer(null);
      }
      setExpandedImage(index);
    },
    [hoverTimer]
  );

  // Handle closing expanded image
  const handleCloseExpanded = React.useCallback(() => {
    setExpandedImage(null);
  }, []);

  // Handle escape key to close
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && expandedImage !== null) {
        handleCloseExpanded();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [expandedImage, handleCloseExpanded]);

  // Cleanup timer on unmount
  React.useEffect(() => {
    return () => {
      if (hoverTimer) {
        clearTimeout(hoverTimer);
      }
    };
  }, [hoverTimer]);

  const slideIn = keyframes`
    from {
      opacity: 0;
      transform: translateX(50px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  `;

  return (
    <Box sx={{ position: 'relative', overflow: 'hidden', borderRadius: 2 }}>
      {productScreenshots.map((screenshot, index) => (
        <Box
          key={index}
          sx={{
            display: currentIndex === index ? 'block' : 'none',
            animation: currentIndex === index ? `${slideIn} 0.6s ease-out` : 'none',
          }}
        >
          <Grid container spacing={{ xs: 2, md: 4 }} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  position: 'relative',
                  borderRadius: 2,
                  overflow: 'hidden',
                  boxShadow: darkMode
                    ? '0 20px 40px rgba(0,0,0,0.4)'
                    : '0 20px 40px rgba(0,0,0,0.1)',
                  cursor: 'pointer',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: { xs: 'none', md: 'scale(1.02)' },
                    boxShadow: darkMode
                      ? '0 25px 50px rgba(0,0,0,0.5)'
                      : '0 25px 50px rgba(0,0,0,0.15)',
                  },
                }}
                onMouseEnter={() => handleImageMouseEnter(index)}
                onMouseLeave={handleImageMouseLeave}
                onClick={() => handleImageClick(index)}
              >
                <img
                  src={darkMode ? screenshot.darkImage : screenshot.lightImage}
                  alt={screenshot.alt}
                  style={{
                    width: '100%',
                    height: 'auto',
                    display: 'block',
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ p: { xs: 1, sm: 2, md: 4 } }}>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    mb: { xs: 1.5, md: 3 },
                    color: amberColors[600],
                    fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' },
                  }}
                >
                  {screenshot.title}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: 'text.secondary',
                    fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem' },
                    lineHeight: 1.6,
                  }}
                >
                  {screenshot.description}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      ))}

      {/* Carousel Indicators */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: 1,
          mt: { xs: 2, md: 3 },
        }}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        {productScreenshots.map((_, index) => (
          <Box
            key={index}
            sx={{
              width: { xs: 10, md: 12 },
              height: { xs: 10, md: 12 },
              borderRadius: '50%',
              bgcolor: currentIndex === index ? amberColors[600] : alpha(amberColors[600], 0.3),
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'scale(1.2)',
                bgcolor: currentIndex === index ? amberColors[700] : alpha(amberColors[600], 0.6),
              },
            }}
            onClick={() => setCurrentIndex(index)}
          />
        ))}
      </Box>

      {/* Expanded Image Modal */}
      {expandedImage !== null && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(0, 0, 0, 0.9)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: { xs: 1, md: 2 },
          }}
          onClick={handleCloseExpanded}
        >
          <Box
            sx={{
              position: 'relative',
              maxWidth: '95vw',
              maxHeight: '95vh',
              borderRadius: 2,
              overflow: 'hidden',
              boxShadow: '0 25px 50px rgba(0,0,0,0.5)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <img
              src={
                darkMode
                  ? productScreenshots[expandedImage].darkImage
                  : productScreenshots[expandedImage].lightImage
              }
              alt={productScreenshots[expandedImage].alt}
              style={{
                width: '100%',
                height: 'auto',
                display: 'block',
                maxWidth: '95vw',
                maxHeight: '95vh',
                objectFit: 'contain',
              }}
            />

            {/* Close Button */}
            <IconButton
              onClick={handleCloseExpanded}
              sx={{
                position: 'absolute',
                top: { xs: 4, md: 8 },
                right: { xs: 4, md: 8 },
                bgcolor: alpha('#000', 0.5),
                color: '#fff',
                minWidth: { xs: 40, md: 48 },
                minHeight: { xs: 40, md: 48 },
                '&:hover': {
                  bgcolor: alpha('#000', 0.7),
                },
              }}
            >
              <CloseIcon />
            </IconButton>

            {/* Image Info */}
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                bgcolor: alpha('#000', 0.7),
                color: '#fff',
                p: { xs: 1.5, md: 2 },
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  mb: 1,
                  fontSize: { xs: '1rem', md: '1.25rem' },
                }}
              >
                {productScreenshots[expandedImage].title}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  opacity: 0.9,
                  fontSize: { xs: '0.75rem', md: '0.875rem' },
                }}
              >
                {productScreenshots[expandedImage].description}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export const LandingPageV2: React.FC = () => {
  const navigate = useNavigate();
  const { darkMode, toggleDarkMode } = useTheme();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  const handleGetStarted = () => {
    navigate('/signup');
  };

  const handleSignIn = () => {
    navigate('/login');
  };

  // Background with blueprint image
  const backgroundStyle = darkMode
    ? {
        background: `
          linear-gradient(135deg, ${alpha('#111827', 0.85)} 0%, ${alpha('#1f2937', 0.85)} 50%, ${alpha('#111827', 0.85)} 100%),
          url('/desktop_blueprint.webp')
        `,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: { xs: 'scroll', md: 'fixed' },
      }
    : {
        background: `
          linear-gradient(135deg, ${alpha(numenorColors[50], 0.78)} 0%, ${alpha(numenorColors[100], 0.78)} 50%, ${alpha(numenorColors[50], 0.78)} 100%),
          url('/desktop_blueprint.webp')
        `,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: { xs: 'scroll', md: 'fixed' },
      };

  // Sidebar content
  const sidebarContent = (
    <Box
      sx={{
        width: { xs: '100vw', sm: 280, md: 256 },
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <Box sx={{ p: { xs: 2, md: 3 }, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: darkMode ? '#ffffff' : amberColors[600],
              fontSize: { xs: '1rem', md: '1.25rem' },
            }}
          >
            Unity Build Estimation
          </Typography>
          <IconButton
            onClick={() => setSidebarOpen(false)}
            size="small"
            sx={{ display: { xs: 'flex', md: 'none' } }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Chip
          label="Beta"
          size="small"
          sx={{
            bgcolor: rarityColors.epic,
            color: 'white',
            fontWeight: 600,
            fontSize: { xs: '0.7rem', md: '0.75rem' },
          }}
        />
      </Box>

      {/* Theme Toggle and Sign In */}
      <Box sx={{ p: { xs: 1.5, md: 2 }, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton onClick={toggleDarkMode} size="small">
              {darkMode ? <Brightness7Icon /> : <Brightness4Icon />}
            </IconButton>
            <Typography
              variant="body2"
              sx={{
                color: darkMode ? '#ffffff' : 'text.secondary',
                fontSize: { xs: '0.8rem', md: '0.875rem' },
              }}
            >
              {darkMode ? 'Light Mode' : 'Dark Mode'}
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          fullWidth
          onClick={handleSignIn}
          sx={{
            bgcolor: amberColors[600],
            fontSize: { xs: '0.875rem', md: '1rem' },
            py: { xs: 1, md: 1.5 },
            '&:hover': { bgcolor: amberColors[700] },
          }}
        >
          Sign In
        </Button>
      </Box>

      {/* Navigation */}
      <Box sx={{ flex: 1, p: { xs: 1.5, md: 2 } }}>
        <List sx={{ p: 0 }}>
          <ListItem
            button
            sx={{
              borderRadius: 1,
              mb: 1,
              py: { xs: 1, md: 1.5 },
              bgcolor: alpha(amberColors[600], 0.1),
              '&:hover': {
                bgcolor: alpha(amberColors[600], 0.2),
                '& .MuiListItemText-primary': {
                  color: darkMode ? '#ffffff' : 'inherit',
                },
                '& .MuiSvgIcon-root': {
                  color: darkMode ? '#ffffff' : amberColors[600],
                },
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: { xs: 36, md: 40 } }}>
              <HomeIcon
                sx={{
                  color: darkMode ? '#ffffff' : amberColors[600],
                  fontSize: { xs: '1.2rem', md: '1.5rem' },
                }}
              />
            </ListItemIcon>
            <ListItemText
              primary="Home"
              sx={{
                '& .MuiTypography-root': {
                  color: darkMode ? '#ffffff' : 'inherit',
                  fontSize: { xs: '0.875rem', md: '1rem' },
                },
              }}
            />
          </ListItem>
          <ListItem
            sx={{
              borderRadius: 1,
              mb: 1,
              py: { xs: 1, md: 1.5 },
              cursor: 'not-allowed',
              opacity: 0.6,
            }}
          >
            <ListItemIcon sx={{ minWidth: { xs: 36, md: 40 } }}>
              <BusinessIcon
                sx={{
                  color: darkMode ? '#ffffff' : 'inherit',
                  fontSize: { xs: '1.2rem', md: '1.5rem' },
                }}
              />
            </ListItemIcon>
            <ListItemText
              primary="The Foundry"
              sx={{
                '& .MuiTypography-root': {
                  color: darkMode ? '#ffffff' : 'inherit',
                  fontSize: { xs: '0.875rem', md: '1rem' },
                },
              }}
            />
            <Chip
              label="Coming Soon!"
              size="small"
              sx={{
                bgcolor: rarityColors.rare,
                color: 'white',
                fontWeight: 600,
                fontSize: { xs: '0.6rem', md: '0.7rem' },
                height: { xs: '18px', md: '20px' },
              }}
            />
          </ListItem>
          <ListItem
            button
            sx={{
              borderRadius: 1,
              mb: 1,
              py: { xs: 1, md: 1.5 },
              '&:hover': {
                bgcolor: alpha(amberColors[600], 0.2),
                '& .MuiListItemText-primary': {
                  color: darkMode ? '#ffffff' : 'inherit',
                },
                '& .MuiSvgIcon-root': {
                  color: darkMode ? '#ffffff' : amberColors[600],
                },
              },
            }}
            onClick={() => navigate('/founders-statement')}
          >
            <ListItemIcon sx={{ minWidth: { xs: 36, md: 40 } }}>
              <PersonIcon
                sx={{
                  color: darkMode ? '#ffffff' : 'inherit',
                  fontSize: { xs: '1.2rem', md: '1.5rem' },
                }}
              />
            </ListItemIcon>
            <ListItemText
              primary="Founder's Statement"
              sx={{
                '& .MuiTypography-root': {
                  color: darkMode ? '#ffffff' : 'inherit',
                  fontSize: { xs: '0.875rem', md: '1rem' },
                },
              }}
            />
          </ListItem>
          <ListItem
            button
            sx={{
              borderRadius: 1,
              mb: 1,
              py: { xs: 1, md: 1.5 },
              '&:hover': {
                bgcolor: alpha(amberColors[600], 0.2),
                '& .MuiListItemText-primary': {
                  color: darkMode ? '#ffffff' : 'inherit',
                },
                '& .MuiSvgIcon-root': {
                  color: darkMode ? '#ffffff' : amberColors[600],
                },
              },
            }}
            onClick={() => navigate('/roadmap')}
          >
            <ListItemIcon sx={{ minWidth: { xs: 36, md: 40 } }}>
              <TimelineIcon
                sx={{
                  color: darkMode ? '#ffffff' : 'inherit',
                  fontSize: { xs: '1.2rem', md: '1.5rem' },
                }}
              />
            </ListItemIcon>
            <ListItemText
              primary="Developer's Roadmap"
              sx={{
                '& .MuiTypography-root': {
                  color: darkMode ? '#ffffff' : 'inherit',
                  fontSize: { xs: '0.875rem', md: '1rem' },
                },
              }}
            />
          </ListItem>
        </List>
      </Box>

      {/* Footer */}
      <Box sx={{ p: { xs: 1.5, md: 2 }, borderTop: 1, borderColor: 'divider' }}>
        <Typography
          variant="caption"
          sx={{
            color: darkMode ? '#ffffff' : 'text.secondary',
            display: 'block',
            mb: 1,
            fontSize: { xs: '0.7rem', md: '0.75rem' },
          }}
        >
          © 2025 Unity Build Estimation
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <WarningIcon sx={{ fontSize: { xs: 14, md: 16 }, color: rarityColors.epic, mr: 0.5 }} />
          <Typography
            variant="caption"
            sx={{
              color: rarityColors.epic,
              fontSize: { xs: '0.7rem', md: '0.75rem' },
            }}
          >
            beta development
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  // Structured data for SEO
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Unity Build Estimation',
    applicationCategory: 'ConstructionApplication',
    operatingSystem: 'Cloud-based',
    offers: {
      '@type': 'Offer',
      price: '125',
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
    },
    description:
      'A comprehensive construction estimation platform with AI assistance and proven methods.',
  };

  return (
    <>
      <Helmet>
        <title>Unity Build Estimation | Construction Estimation Platform</title>
        <meta
          name="description"
          content="Unity Build Estimation honors the timeless bond between those who build and those who learn. Comprehensive construction estimation with AI assistance."
        />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      {/* Mobile Sidebar */}
      <Drawer
        anchor="left"
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100vw', sm: 280, md: 256 },
            bgcolor: darkMode ? 'background.paper' : 'background.default',
          },
        }}
      >
        {sidebarContent}
      </Drawer>

      {/* Main Layout */}
      <Box
        sx={{
          minHeight: '100vh',
          ...backgroundStyle,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Background Pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.03,
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            pointerEvents: 'none',
          }}
        />

        {/* Mobile Menu Button */}
        <Box
          sx={{
            position: 'fixed',
            top: { xs: 12, md: 16 },
            left: { xs: 12, md: 16 },
            zIndex: 1000,
            display: { xs: 'block', md: 'none' },
          }}
        >
          <IconButton
            onClick={() => setSidebarOpen(true)}
            sx={{
              bgcolor: alpha(darkMode ? '#1a1a1a' : '#ffffff', 0.9),
              backdropFilter: 'blur(10px)',
              border: 1,
              borderColor: 'divider',
              minWidth: { xs: 44, sm: 48 },
              minHeight: { xs: 44, sm: 48 },
              '&:hover': {
                bgcolor: alpha(darkMode ? '#2d2d2d' : '#f5f5f5', 0.9),
              },
            }}
          >
            <MenuIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Desktop Sidebar */}
        <Box
          sx={{
            position: 'fixed',
            left: 0,
            top: 0,
            bottom: 0,
            width: 256,
            zIndex: 900,
            display: { xs: 'none', md: 'block' },
            bgcolor: darkMode ? 'background.paper' : 'background.default',
            borderRight: 1,
            borderColor: 'divider',
          }}
        >
          {sidebarContent}
        </Box>

        {/* Main Content */}
        <Box
          sx={{
            ml: { xs: 0, md: '256px' },
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Container
            maxWidth="lg"
            sx={{
              flex: 1,
              py: { xs: 2, sm: 3, md: 6 },
              px: { xs: 1, sm: 2, md: 3 },
            }}
          >
            {/* Hero Section - More Subtle */}
            <Box sx={{ textAlign: 'center', mb: { xs: 4, sm: 5, md: 8 } }}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: '1.5rem', sm: '2rem', md: '2.75rem' },
                  mb: { xs: 2, md: 3 },
                  color: 'text.primary',
                  px: { xs: 1, sm: 0 },
                }}
              >
                Building the Future of Trades Estimation
              </Typography>

              <Typography
                variant="h6"
                sx={{
                  color: 'text.secondary',
                  fontSize: { xs: '0.9rem', sm: '1rem', md: '1.25rem' },
                  mb: { xs: 3, md: 4 },
                  maxWidth: '800px',
                  mx: 'auto',
                  fontWeight: 300,
                  px: { xs: 1, sm: 0 },
                }}
              >
                "As masters once passed their knowledge to apprentices, we continue that tradition."
              </Typography>

              {/* Personal Developer Message */}
              <Box
                sx={{
                  bgcolor: darkMode ? alpha('#000', 0.2) : alpha('#fff', 0.7),
                  borderRadius: 2,
                  p: { xs: 2, sm: 3, md: 4 },
                  mb: { xs: 3, md: 4 },
                  maxWidth: '900px',
                  mx: 'auto',
                  border: 1,
                  borderColor: alpha(amberColors[600], 0.2),
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem' },
                    color: 'text.primary',
                    lineHeight: 1.7,
                    mb: { xs: 1.5, md: 2 },
                  }}
                >
                  Welcome, fellow craftspeople. I'm building this platform from the ground up to be
                  the finest estimation tool you'll ever use. My commitment is simple: create
                  software that's affordable, intuitive, and respects your time and expertise.
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: { xs: '0.8rem', sm: '0.875rem', md: '1rem' },
                    color: 'text.secondary',
                    lineHeight: 1.6,
                    fontStyle: 'italic',
                  }}
                >
                  This is a journey I'll be perfecting for years to come. If you're willing to take
                  a chance on something new, I'd be honored to have you join me in shaping the
                  future of our craft.
                </Typography>
              </Box>
            </Box>

            {/* Product Showcase Carousel */}
            <Box sx={{ mb: { xs: 4, sm: 5, md: 8 } }}>
              <Typography
                variant="h5"
                sx={{
                  textAlign: 'center',
                  color: 'text.secondary',
                  mb: { xs: 3, md: 6 },
                  fontWeight: 500,
                  fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' },
                  px: { xs: 1, sm: 0 },
                }}
              >
                What We're Building Together
              </Typography>
              <ProductCarousel />
            </Box>

            {/* Final CTA */}
            <Box sx={{ textAlign: 'center' }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleGetStarted}
                disabled={!launched}
                sx={{
                  bgcolor: amberColors[600],
                  py: { xs: 1.5, md: 2 },
                  px: { xs: 3, md: 4 },
                  fontSize: { xs: '1rem', md: '1.125rem' },
                  fontWeight: 600,
                  '&:hover': {
                    bgcolor: amberColors[700],
                    transform: 'translateY(-2px)',
                  },
                  '&.Mui-disabled': {
                    bgcolor: alpha(amberColors[600], 0.5),
                  },
                }}
              >
                Create Account
              </Button>
            </Box>
          </Container>
        </Box>
      </Box>
    </>
  );
};

export default LandingPageV2;
